import { Const } from "../../Const";
import { Bezier } from "../../maths/Bezier";
import { Matrix } from "../../maths/Matrix";
import { Rectangle } from "../../maths/Rectangle";
import { Vector4 } from "../../maths/Vector4";
import { Texture } from "../../resource/Texture";
import { Texture2D } from "../../resource/Texture2D";
import { BlendMode } from "../../webgl/canvas/BlendMode";
import { DrawStyle } from "../../webgl/canvas/DrawStyle";
import { Path } from "../../webgl/canvas/Path";
import { SaveBase } from "../../webgl/canvas/save/SaveBase";
import { SaveClipRect } from "../../webgl/canvas/save/SaveClipRect";
import { SaveStyle } from "../../webgl/canvas/save/SaveStyle";
import { SaveMark } from "../../webgl/canvas/save/SaveMark";
import { SaveTransform } from "../../webgl/canvas/save/SaveTransform";
import { SaveTranslate } from "../../webgl/canvas/save/SaveTranslate";
import { BasePoly } from "../../webgl/shapes/BasePoly";
import { Earcut } from "../../webgl/shapes/Earcut";
import { SubmitBase } from "../../webgl/submit/SubmitBase";
import { SubmitKey } from "../../webgl/submit/SubmitKey";
import { TextRender } from "../../webgl/text/TextRender";
import { GraphicsMesh } from "../../webgl/utils/GraphicsMesh";
import { LayaGL } from "../../layagl/LayaGL";
import { MeshTopology } from "../../RenderEngine/RenderEnum/RenderPologyMode";
import { DrawType } from "../../RenderEngine/RenderEnum/DrawType";
import { IndexFormat } from "../../RenderEngine/RenderEnum/IndexFormat";
import { BufferUsage } from "../../RenderEngine/RenderEnum/BufferTargetType";
const defaultClipMatrix = new Matrix(Const.MAX_CLIP_SIZE, 0, 0, Const.MAX_CLIP_SIZE, 0, 0);
const tmpMat = new Matrix();
const _drawTexToDrawTri_Vert = new Float32Array(8);
const _drawTexToDrawTri_Index = new Uint16Array([0, 1, 2, 0, 2, 3]);
const _drawTexToQuad_Index = new Uint16Array([0, 2, 1, 0, 3, 2]);
export class GraphicsRunner {
    constructor() {
        this._alpha = 1.0;
        this._material = null;
        this._fillStyle = DrawStyle.DEFAULT;
        this._strokeStyle = DrawStyle.DEFAULT;
        this._tempUV = new Float32Array(8);
        this._drawTriUseAbsMatrix = false;
        this._other = null;
        this._path = null;
        this._curSubmit = null;
        this._submitKey = new SubmitKey();
        this._graphicsData = null;
        this._transedPoints = new Array(8);
        this._temp4Points = new Array(8);
        this._clipRect = SaveClipRect.MAX;
        this._globalClipMatrix = defaultClipMatrix.clone();
        this._clip_x = 0;
        this._clip_y = 0;
        this._clipInfoID = 0;
        this._clipID_Gen = 0;
        this._meshPool = [];
        this._matrixChanged = false;
        this._matBuffer = new Float32Array(6);
        this._lastMatScaleX = 1.0;
        this._lastMatScaleY = 1.0;
        this._lastMat_a = 1.0;
        this._lastMat_b = 0.0;
        this._lastMat_c = 0.0;
        this._lastMat_d = 1.0;
        this._nBlendType = BlendMode.normal;
        this._save = null;
        this._saveMark = null;
        this.sprite = null;
        this._textRender = null;
        this._italicDeg = 0;
        this._lastTex = null;
        this._defTexture = null;
        this.drawTexAlign = false;
        this._defTexture = new Texture(Texture2D.whiteTexture);
        this._textRender = new TextRender();
        this._other = ContextParams.DEFAULT;
        this._curMat = Matrix.create();
        this._save = [SaveMark.Create(this)];
        this._save.length = 10;
        this.clear();
        this.initDefalutMesh();
    }
    get lineJoin() {
        return '';
    }
    set lineJoin(value) {
    }
    get lineCap() {
        return '';
    }
    set lineCap(value) {
    }
    get miterLimit() {
        return '';
    }
    set miterLimit(value) {
    }
    touchRes(res) {
        this._graphicsData.touchRes(res);
    }
    transformByMatrix(matrix, tx, ty) {
        this.transform(matrix.a, matrix.b, matrix.c, matrix.d, matrix.tx + tx, matrix.ty + ty);
    }
    drawRect(x, y, width, height, fillColor, lineColor, lineWidth) {
        var runner = this;
        if (fillColor != null) {
            runner.fillStyle = fillColor;
            runner.fillRect(x, y, width, height);
        }
        if (lineColor != null) {
            runner.strokeStyle = lineColor;
            runner.lineWidth = lineWidth;
            runner.strokeRect(x, y, width, height);
        }
    }
    alpha(value) {
        this.globalAlpha *= value;
    }
    _transform(mat, pivotX, pivotY) {
        this.translate(pivotX, pivotY);
        this.transform(mat.a, mat.b, mat.c, mat.d, mat.tx, mat.ty);
        this.translate(-pivotX, -pivotY);
    }
    _rotate(angle, pivotX, pivotY) {
        this.translate(pivotX, pivotY);
        this.rotate(angle);
        this.translate(-pivotX, -pivotY);
    }
    _scale(scaleX, scaleY, pivotX, pivotY) {
        this.translate(pivotX, pivotY);
        this.scale(scaleX, scaleY);
        this.translate(-pivotX, -pivotY);
    }
    _drawLine(x, y, fromX, fromY, toX, toY, lineColor, lineWidth, vid) {
        this.beginPath();
        this.strokeStyle = lineColor;
        this.lineWidth = lineWidth;
        this.moveTo(x + fromX, y + fromY);
        this.lineTo(x + toX, y + toY);
        this.stroke();
    }
    _drawLines(x, y, points, lineColor, lineWidth, vid) {
        this.beginPath();
        this.strokeStyle = lineColor;
        this.lineWidth = lineWidth;
        this.addPath(points.slice(), false, false, x, y);
        this.stroke();
    }
    drawCurves(x, y, points, lineColor, lineWidth) {
        this.beginPath();
        this.strokeStyle = lineColor;
        this.lineWidth = lineWidth;
        this.moveTo(x + points[0], y + points[1]);
        var i = 2, n = points.length;
        while (i < n) {
            this.quadraticCurveTo(x + points[i++], y + points[i++], x + points[i++], y + points[i++]);
        }
        this.stroke();
    }
    _fillAndStroke(fillColor, strokeColor, lineWidth, isConvexPolygon = false) {
        if (fillColor != null) {
            this.fillStyle = fillColor;
            this.fill();
        }
        if (strokeColor != null && lineWidth > 0) {
            this.strokeStyle = strokeColor;
            this.lineWidth = lineWidth;
            this.stroke();
        }
    }
    _drawCircle(x, y, radius, fillColor, lineColor, lineWidth, vid) {
        this.beginPath(true);
        this.arc(x, y, radius, radius, 0, 2 * Math.PI, false, true, 40);
        this.closePath();
        this._fillAndStroke(fillColor, lineColor, lineWidth);
    }
    _drawEllipse(x, y, width, height, fillColor, lineColor, lineWidth) {
        this.beginPath(true);
        this.arc(x, y, width, height, 0, 2 * Math.PI, false, true, 40);
        this.closePath();
        this._fillAndStroke(fillColor, lineColor, lineWidth);
    }
    _drawRoundRect(x, y, width, height, lt, rt, lb, rb, fillColor, lineColor, lineWidth) {
        if (width <= 0)
            return;
        if (height <= 0)
            return;
        this.beginPath(true);
        var tPath = this._getPath();
        if (0 >= lt) {
            tPath.addPoint(x, y);
        }
        else {
            let st = Math.PI;
            let ed = 1.5 * Math.PI;
            if (width < lt + rt) {
                let dxlt = lt * (lt + rt - width) / (lt + rt);
                let hity = Math.sqrt(lt * lt - dxlt * dxlt);
                let ang = Math.atan2(hity, dxlt);
                let dAng = 0.5 * Math.PI - ang;
                ed -= dAng;
            }
            if (height < lt + lb) {
                let dylt = lt * (lt + lb - height) / (lt + lb);
                let hitx = Math.sqrt(lt * lt - dylt * dylt);
                let ang = Math.atan2(dylt, hitx);
                st += ang;
            }
            if (st > ed) {
            }
            else {
                this.arc(x + lt, y + lt, lt, lt, st, ed, false, true, 5);
            }
        }
        let startX = x + width - rt;
        if (0 >= rt) {
            tPath.addPoint(startX, y);
        }
        else {
            let st = 1.5 * Math.PI;
            let ed = 2 * Math.PI;
            if (width < lt + rt) {
                let dxlt = rt * (lt + rt - width) / (lt + rt);
                let hity = Math.sqrt(lt * lt - dxlt * dxlt);
                let ang = Math.atan2(hity, dxlt);
                let dAng = 0.5 * Math.PI - ang;
                st += dAng;
            }
            if (height < rt + rb) {
                let dyrt = rt * (rt + rb - height) / (rt + rb);
                let hitx = Math.sqrt(rt * rt - dyrt * dyrt);
                let ang = Math.atan2(dyrt, hitx);
                ed -= ang;
            }
            if (st > ed) {
            }
            else {
                this.arc(startX, y + rt, rt, rt, st, ed, false, true, 5);
            }
        }
        startX = x + width - rb;
        let startY = y + height - rb;
        if (0 >= rb) {
            tPath.addPoint(startX, startY);
        }
        else {
            let st = 0;
            let ed = 0.5 * Math.PI;
            if (width < lb + rb) {
                let dxlb = rb * (lb + rb - width) / (lb + rb);
                let hity = Math.sqrt(lb * lb - dxlb * dxlb);
                let ang = Math.atan2(hity, dxlb);
                let dAng = 0.5 * Math.PI - ang;
                ed -= dAng;
            }
            if (height < rt + rb) {
                let dyrt = rb * (rt + rb - height) / (rt + rb);
                let hitx = Math.sqrt(rb * rb - dyrt * dyrt);
                let ang = Math.atan2(dyrt, hitx);
                st += ang;
            }
            if (st > ed) {
            }
            else {
                this.arc(startX, startY, rb, rb, st, ed, false, true, 5);
            }
        }
        startX = x + lb;
        startY = y + height - lb;
        if (0 >= lb) {
            tPath.addPoint(startX, startY);
        }
        else {
            let st = 0.5 * Math.PI;
            let ed = Math.PI;
            if (width < lb + rb) {
                let dxlb = rb * (lb + rb - width) / (lb + rb);
                let hity = Math.sqrt(lb * lb - dxlb * dxlb);
                let ang = Math.atan2(hity, dxlb);
                let dAng = 0.5 * Math.PI - ang;
                st += dAng;
            }
            if (height < lt + lb) {
                let dylt = lb * (lt + lb - height) / (lt + lb);
                let hitx = Math.sqrt(lb * lb - dylt * dylt);
                let ang = Math.atan2(dylt, hitx);
                ed -= ang;
            }
            if (st > ed) {
            }
            else {
                this.arc(startX, startY, lb, lb, st, ed, false, true, 5);
            }
        }
        this.closePath();
        this._fillAndStroke(fillColor, lineColor, lineWidth);
    }
    _drawPie(x, y, radius, startAngle, endAngle, fillColor, lineColor, lineWidth, vid) {
        this.beginPath();
        this.moveTo(x, y);
        this.arc(x, y, radius, radius, startAngle, endAngle);
        this.closePath();
        this._fillAndStroke(fillColor, lineColor, lineWidth);
    }
    _drawPoly(x, y, points, fillColor, lineColor, lineWidth, isConvexPolygon, vid) {
        this.beginPath();
        this.addPath(points.slice(), true, isConvexPolygon, x, y);
        this.closePath();
        this._fillAndStroke(fillColor, lineColor, lineWidth, isConvexPolygon);
    }
    _drawPath(x, y, paths, brush, pen) {
        this.beginPath();
        for (var i = 0, n = paths.length; i < n; i++) {
            var path = paths[i];
            switch (path[0]) {
                case "moveTo":
                    this.moveTo(x + path[1], y + path[2]);
                    break;
                case "lineTo":
                    this.lineTo(x + path[1], y + path[2]);
                    break;
                case "arcTo":
                    this.arcTo(x + path[1], y + path[2], x + path[3], y + path[4], path[5]);
                    break;
                case "closePath":
                    this.closePath();
                    break;
            }
        }
        if (brush != null) {
            this.fillStyle = brush.fillStyle;
            this.fill();
        }
        if (pen != null) {
            this.strokeStyle = pen.strokeStyle;
            this.lineWidth = pen.lineWidth || 1;
            this.lineJoin = pen.lineJoin;
            this.lineCap = pen.lineCap;
            this.miterLimit = pen.miterLimit;
            this.stroke();
        }
    }
    destroy() {
        for (let i = 0; i < this._meshPool.length; i++) {
            this._meshPool[i].destroy();
        }
        this._meshPool = null;
        this.sprite = null;
        this._path = null;
        this._save = null;
    }
    clear() {
        this._submitKey.clear();
        this._curSubmit = SubmitBase.RENDERBASE;
        this._curMat.identity();
        this._other = ContextParams.DEFAULT;
        this._other.clear();
        this._clipRect = SaveClipRect.MAX;
        this._clip_x = 0;
        this._clip_y = 0;
        this._alpha = 1.0;
        this._nBlendType = BlendMode.normal;
        this._fillStyle = this._strokeStyle = DrawStyle.DEFAULT;
        this._lastTex = null;
        this._saveMark = this._save[0];
        this._save._length = 1;
    }
    getCurrentScaleX() {
        let scaleX = this.getMatScaleX();
        if (this.sprite && this.sprite.globalTrans) {
            const matrix = this.sprite.globalTrans.getMatrix();
            scaleX *= Math.hypot(matrix.a, matrix.b);
        }
        return Math.abs(scaleX);
    }
    getCurrentScaleY() {
        let scaleY = this.getMatScaleY();
        if (this.sprite && this.sprite.globalTrans) {
            const matrix = this.sprite.globalTrans.getMatrix();
            scaleY *= Math.hypot(matrix.c, matrix.d);
        }
        return Math.abs(scaleY);
    }
    getMatScaleX() {
        if (this._lastMat_a === this._curMat.a && this._lastMat_b === this._curMat.b)
            return this._lastMatScaleX;
        this._lastMatScaleX = this._curMat.getScaleX();
        this._lastMat_a = this._curMat.a;
        this._lastMat_b = this._curMat.b;
        return this._lastMatScaleX;
    }
    getMatScaleY() {
        if (this._lastMat_c === this._curMat.c && this._lastMat_d === this._curMat.d)
            return this._lastMatScaleY;
        this._lastMatScaleY = this._curMat.getScaleY();
        this._lastMat_c = this._curMat.c;
        this._lastMat_d = this._curMat.d;
        return this._lastMatScaleY;
    }
    set fillStyle(value) {
        if (!this._fillStyle.equal(value)) {
            SaveStyle.save(this, "fillStyle");
            this._fillStyle = DrawStyle.create(value);
            this._submitKey.other = -this._fillStyle._color.numColor;
        }
    }
    get fillStyle() {
        return this._fillStyle;
    }
    set globalAlpha(value) {
        value = Math.floor(value * 1000) / 1000;
        if (value != this._alpha) {
            SaveBase.save(this, SaveBase.TYPE_ALPHA, this, false);
            this._alpha = value;
        }
    }
    get globalAlpha() {
        return this._alpha;
    }
    set textAlign(value) {
        (this._other.textAlign === value) || (this._other = this._other.make(), SaveBase.save(this, SaveBase.TYPE_TEXTALIGN, this._other, false), this._other.textAlign = value);
    }
    get textAlign() {
        return this._other.textAlign;
    }
    set textBaseline(value) {
        (this._other.textBaseline === value) || (this._other = this._other.make(), SaveBase.save(this, SaveBase.TYPE_TEXTBASELINE, this._other, false), this._other.textBaseline = value);
    }
    get textBaseline() {
        return this._other.textBaseline;
    }
    set globalCompositeOperation(value) {
        value == null || (this._nBlendType === value) || (SaveBase.save(this, SaveBase.TYPE_GLOBALCOMPOSITEOPERATION, this, true), this._curSubmit = SubmitBase.RENDERBASE, this._nBlendType = value);
    }
    get globalCompositeOperation() {
        return this._nBlendType;
    }
    set strokeStyle(value) {
        this._strokeStyle.equal(value) || (SaveStyle.save(this, "strokeStyle"), this._strokeStyle = DrawStyle.create(value), this._submitKey.other = -this._strokeStyle._color.numColor);
    }
    get strokeStyle() {
        return this._strokeStyle;
    }
    translate(x, y) {
        if (x !== 0 || y !== 0) {
            SaveTranslate.save(this);
            if (this._curMat._bTransform) {
                SaveTransform.save(this);
                this._curMat.tx += (x * this._curMat.a + y * this._curMat.c);
                this._curMat.ty += (x * this._curMat.b + y * this._curMat.d);
            }
            else {
                this._curMat.tx = x;
                this._curMat.ty = y;
            }
            this._matrixChanged = true;
        }
    }
    set lineWidth(value) {
        (this._other.lineWidth === value) || (this._other = this._other.make(), SaveBase.save(this, SaveBase.TYPE_LINEWIDTH, this._other, false), this._other.lineWidth = value);
    }
    get lineWidth() {
        return this._other.lineWidth;
    }
    save() {
        this._save[this._save._length++] = SaveMark.Create(this);
    }
    restore() {
        var sz = this._save._length;
        var lastBlend = this._nBlendType;
        if (sz < 1)
            return;
        for (var i = sz - 1; i >= 0; i--) {
            var o = this._save[i];
            o.restore(this);
            if (o.isSaveMark()) {
                this._save._length = i;
                return;
            }
        }
        if (lastBlend != this._nBlendType) {
            this.breakNextMerge();
        }
    }
    fillText(txt, x, y, fontStr, color, align, lineWidth = 0, borderColor = "") {
        this._textRender.filltext(this, txt, x, y, fontStr, color, borderColor, lineWidth, align);
    }
    drawText(text, x, y, font, color, textAlign) {
        this._textRender.filltext(this, text, x, y, font, color, null, 0, textAlign);
    }
    strokeWord(text, x, y, font, color, lineWidth, textAlign) {
        this._textRender.filltext(this, text, x, y, font, null, color, lineWidth, textAlign);
    }
    fillBorderText(txt, x, y, font, color, borderColor, lineWidth, textAlign) {
        this._textRender.filltext(this, txt, x, y, font, color, borderColor, lineWidth, textAlign);
    }
    _fast_filltext(data, x, y, fontObj, color, strokeColor, lineWidth, textAlign) {
        this._textRender._fast_filltext(this, data, x, y, fontObj, color, strokeColor, lineWidth, textAlign);
    }
    _fillRect(x, y, width, height, rgba) {
        var submit = this._curSubmit;
        this.transformQuad(x, y, width, height, 0, this._curMat, this._transedPoints);
        let vertexResult = this.acquire(4);
        let mesh = vertexResult.mesh;
        var sameKey = submit && (submit.mesh === mesh
            && submit._key.blendShader === this._nBlendType);
        sameKey && (sameKey = sameKey && this.isSameClipInfo(submit));
        if (!this.clipedOff(this._transedPoints)) {
            if (!sameKey) {
                submit = this._curSubmit = this.createSubmit(mesh);
                let material = submit._internalInfo;
                this._setClipInfo(material);
                submit.clipInfoID = this._clipInfoID;
                material.textureHost = this._lastTex;
                submit._key.other = (this._lastTex && this._lastTex.bitmap) ? this._lastTex.bitmap.id : -1;
            }
            this.appendData(this._transedPoints, _drawTexToQuad_Index, vertexResult, submit, null, rgba, null, null, false);
            this._appendBlockInfo(vertexResult);
        }
    }
    _appendBlockInfo(info) {
        this._curSubmit.appendData(info);
    }
    fillRect(x, y, width, height, fillStyle = null) {
        var drawstyle = fillStyle ? DrawStyle.create(fillStyle) : this._fillStyle;
        var rgba = this.mixRGBandAlpha(drawstyle._color.numColor);
        this._fillRect(x, y, width, height, rgba);
    }
    fillTexture(texture, x, y, width, height, type, offset, color) {
        if (!this._getImageSource(texture)) {
            return;
        }
        this._fillTexture(texture, texture.width, texture.height, texture.uvrect, x, y, width, height, type, offset.x, offset.y, color);
    }
    _fillTexture(texture, texw, texh, texuvRect, x, y, width, height, type, offsetx, offsety, color) {
        var submit = this._curSubmit;
        let vertexResult = this.acquire(4);
        let mesh = vertexResult.mesh;
        var repeatx = true;
        var repeaty = true;
        switch (type) {
            case "repeat": break;
            case "repeat-x":
                repeaty = false;
                break;
            case "repeat-y":
                repeatx = false;
                break;
            case "no-repeat":
                repeatx = repeaty = false;
                break;
            default: break;
        }
        var uv = this._temp4Points;
        var stu = 0;
        var stv = 0;
        var stx = 0, sty = 0, edx = 0, edy = 0;
        if (offsetx < 0) {
            stx = x;
            stu = (-offsetx % texw) / texw;
        }
        else {
            stx = x + offsetx;
        }
        if (offsety < 0) {
            sty = y;
            stv = (-offsety % texh) / texh;
        }
        else {
            sty = y + offsety;
        }
        edx = x + width;
        edy = y + height;
        (!repeatx) && (edx = Math.min(edx, x + offsetx + texw));
        (!repeaty) && (edy = Math.min(edy, y + offsety + texh));
        if (edx < x || edy < y)
            return;
        if (stx > edx || sty > edy)
            return;
        var edu = (edx - x - offsetx) / texw;
        var edv = (edy - y - offsety) / texh;
        this.transformQuad(stx, sty, edx - stx, edy - sty, 0, this._curMat, this._transedPoints);
        uv[0] = stu;
        uv[1] = stv;
        uv[2] = edu;
        uv[3] = stv;
        uv[4] = edu;
        uv[5] = edv;
        uv[6] = stu;
        uv[7] = edv;
        if (!this.clipedOff(this._transedPoints)) {
            submit = this._curSubmit = this.createSubmit(mesh);
            let material = submit._internalInfo;
            material.fillTexture = true;
            var arry = texuvRect.concat();
            Vector4.TEMP.setValue(arry[0], arry[1], arry[2], arry[3]);
            material.u_TexRange = Vector4.TEMP;
            this._setClipInfo(material);
            submit.clipInfoID = this._clipInfoID;
            submit._internalInfo.textureHost = texture;
            var rgba = this._mixRGBandAlpha(color, this._alpha);
            this.appendData(this._transedPoints, _drawTexToQuad_Index, vertexResult, submit, uv, rgba, null, null, true);
            this._appendBlockInfo(vertexResult);
        }
        this.breakNextMerge();
    }
    createSubmit(mesh) {
        return this._graphicsData.createSubmit(this, mesh, this._material);
    }
    drawTexture(tex, x, y, width, height, color = 0xffffffff) {
        this._drawTextureM(tex, x, y, width, height, null, 1, null, color);
    }
    drawTextures(tex, pos, tx, ty, colors) {
        if (!this._getImageSource(tex)) {
            return;
        }
        var n = pos.length / 2;
        var ipos = 0;
        var bmpid = tex.bitmap.id;
        for (var i = 0; i < n; i++) {
            const color = typeof colors[i] === 'number' ? colors[i] : 0xffffffff;
            this._inner_drawTexture(tex, bmpid, pos[ipos++] + tx, pos[ipos++] + ty, 0, 0, null, null, 1.0, false, color);
        }
    }
    _drawTextureM(tex, x, y, width, height, m, alpha, uv, color) {
        if (!this._getImageSource(tex)) {
            return false;
        }
        return this._inner_drawTexture(tex, tex.bitmap.id, x, y, width, height, m, uv, alpha, false, color);
    }
    _setClipInfo(material) {
        if (this._clipRect === SaveClipRect.MAX) {
            material.materialClip = false;
            return;
        }
        let clipInfo = this._globalClipMatrix;
        var cm = material.clipMatDir;
        material.materialClip = true;
        cm.x = clipInfo.a;
        cm.y = clipInfo.b;
        cm.z = clipInfo.c;
        cm.w = clipInfo.d;
        material.clipMatDir = cm;
        var cmp = material.clipMatPos;
        cmp.x = clipInfo.tx;
        cmp.y = clipInfo.ty, cmp.z = this._clip_x, cmp.w = this._clip_y;
        material.clipMatPos = cmp;
    }
    isSameClipInfo(submit) {
        return submit.clipInfoID !== this._clipInfoID;
    }
    _inner_drawTexture(tex, imgid, x, y, width, height, m, uv, alpha, lastRender, color) {
        if (width <= 0 || height <= 0) {
            return false;
        }
        var preKey = this._curSubmit._key;
        uv = uv || tex._uv;
        if (preKey.other === imgid) {
            let xoff = 0;
            if (this._italicDeg != 0) {
                xoff = Math.tan(this._italicDeg * Math.PI / 180) * height;
            }
            var tv = _drawTexToDrawTri_Vert;
            tv[0] = x + xoff;
            tv[1] = y;
            tv[2] = x + width + xoff, tv[3] = y, tv[4] = x + width, tv[5] = y + height, tv[6] = x, tv[7] = y + height;
            this._drawTriUseAbsMatrix = true;
            var tuv = this._tempUV;
            tuv[0] = uv[0];
            tuv[1] = uv[1];
            tuv[2] = uv[2];
            tuv[3] = uv[3];
            tuv[4] = uv[4];
            tuv[5] = uv[5];
            tuv[6] = uv[6];
            tuv[7] = uv[7];
            this.drawTriangles(tex, 0, 0, tv, tuv, _drawTexToDrawTri_Index, m || this._curMat, alpha, null, color);
            this._drawTriUseAbsMatrix = false;
            return true;
        }
        var submit = this._curSubmit;
        var ops = this._transedPoints;
        this.transformQuad(x, y, width || tex.width, height || tex.height, this._italicDeg, m || this._curMat, ops);
        if (this.drawTexAlign) {
            var round = Math.round;
            ops[0] = round(ops[0]);
            ops[1] = round(ops[1]);
            ops[2] = round(ops[2]);
            ops[3] = round(ops[3]);
            ops[4] = round(ops[4]);
            ops[5] = round(ops[5]);
            ops[6] = round(ops[6]);
            ops[7] = round(ops[7]);
            this.drawTexAlign = false;
        }
        var rgba = this._mixRGBandAlpha(color, this._alpha * alpha);
        let vertexResult = this.acquire(4);
        let mesh = vertexResult.mesh;
        let sameKey = (imgid >= 0
            && submit.mesh === mesh
            && preKey.other === imgid)
            && !this.isSameClipInfo(this._curSubmit);
        this._lastTex = tex;
        if (!sameKey) {
            this._curSubmit = submit = this.createSubmit(mesh);
            let material = submit._internalInfo;
            this._setClipInfo(material);
            material.textureHost = tex;
            submit._key.other = imgid;
            submit.clipInfoID = this._clipInfoID;
        }
        this.appendData(ops, _drawTexToQuad_Index, vertexResult, submit, uv, rgba, null, null, true);
        this._appendBlockInfo(vertexResult);
        return true;
    }
    clipedOff(pt) {
        if (this._clipRect.width <= 0 || this._clipRect.height <= 0)
            return true;
        return false;
    }
    transformQuad(x, y, w, h, italicDeg, m, out) {
        var xoff = 0;
        if (italicDeg != 0) {
            xoff = Math.tan(italicDeg * Math.PI / 180) * h;
        }
        var maxx = x + w;
        var maxy = y + h;
        var tx = m.tx;
        var ty = m.ty;
        var ma = m.a;
        var mb = m.b;
        var mc = m.c;
        var md = m.d;
        var a0 = x + xoff;
        var a1 = y;
        var a2 = maxx + xoff;
        var a3 = y;
        var a4 = maxx;
        var a5 = maxy;
        var a6 = x;
        var a7 = maxy;
        if (m !== this._curMat
            || this._matrixChanged) {
            if (m._bTransform) {
                out[0] = a0 * ma + a1 * mc + tx;
                out[1] = a0 * mb + a1 * md + ty;
                out[2] = a2 * ma + a3 * mc + tx;
                out[3] = a2 * mb + a3 * md + ty;
                out[4] = a4 * ma + a5 * mc + tx;
                out[5] = a4 * mb + a5 * md + ty;
                out[6] = a6 * ma + a7 * mc + tx;
                out[7] = a6 * mb + a7 * md + ty;
            }
            else {
                out[0] = a0 + tx;
                out[1] = a1 + ty;
                out[2] = a2 + tx;
                out[3] = a3 + ty;
                out[4] = a4 + tx;
                out[5] = a5 + ty;
                out[6] = a6 + tx;
                out[7] = a7 + ty;
            }
        }
        else {
            out[0] = a0;
            out[1] = a1;
            out[2] = a2;
            out[3] = a3;
            out[4] = a4;
            out[5] = a5;
            out[6] = a6;
            out[7] = a7;
        }
    }
    breakNextMerge() {
        this._curSubmit = SubmitBase.RENDERBASE;
    }
    drawTextureWithTransform(tex, x, y, width, height, transform, tx, ty, alpha, blendMode, uv, color = 0xffffffff) {
        var _a;
        var oldcomp;
        var curMat = this._curMat;
        if (blendMode != null) {
            if (typeof blendMode == "string")
                blendMode = (_a = BlendMode[blendMode]) !== null && _a !== void 0 ? _a : (blendMode === "destination-out" ? BlendMode.destinationOut : 0);
            oldcomp = this.globalCompositeOperation;
            this.globalCompositeOperation = blendMode;
        }
        if (!transform) {
            this._drawTextureM(tex, x + tx, y + ty, width, height, curMat, alpha, uv, color);
            if (blendMode) {
                this.globalCompositeOperation = oldcomp;
            }
            return;
        }
        tmpMat.a = transform.a;
        tmpMat.b = transform.b;
        tmpMat.c = transform.c;
        tmpMat.d = transform.d;
        tmpMat.tx = transform.tx + tx;
        tmpMat.ty = transform.ty + ty;
        tmpMat._bTransform = transform._bTransform;
        if (transform && curMat._bTransform) {
            Matrix.mul(tmpMat, curMat, tmpMat);
            transform = tmpMat;
            transform._bTransform = true;
        }
        else {
            tmpMat.tx += curMat.tx;
            tmpMat.ty += curMat.ty;
            transform = tmpMat;
        }
        this._drawTextureM(tex, x, y, width, height, transform, alpha, uv, color);
        if (blendMode != null)
            this.globalCompositeOperation = oldcomp;
    }
    drawTriangles(tex, x, y, vertices, uvs, indices, matrix, alpha, blendMode, colorNum, colors, uvRange) {
        var _a;
        if (!this._getImageSource(tex)) {
            return;
        }
        if (alpha == null)
            alpha = 1.0;
        if (colorNum == null)
            colorNum = 0xffffffff;
        let oldcomp = null;
        if (blendMode != null) {
            if (typeof blendMode == "string")
                blendMode = (_a = BlendMode[blendMode]) !== null && _a !== void 0 ? _a : (blendMode === "destination-out" ? BlendMode.destinationOut : 0);
            oldcomp = this.globalCompositeOperation;
            this.globalCompositeOperation = blendMode;
        }
        let vertexCount = vertices.length / 2;
        let vertexResult = this.acquire(vertexCount);
        let mesh = vertexResult.mesh;
        var webGLImg = tex instanceof Texture ? tex.bitmap : tex;
        var preKey = this._curSubmit._key;
        var sameKey = this._curSubmit.mesh === mesh
            && preKey.other === webGLImg.id
            && preKey.blendShader === this._nBlendType;
        let submit = this._curSubmit;
        if (!sameKey) {
            submit = this._curSubmit = this.createSubmit(mesh);
            submit._internalInfo.textureHost = tex;
            this._setClipInfo(submit._internalInfo);
            submit._key.other = webGLImg.id;
            submit.clipInfoID = this._clipInfoID;
        }
        var rgba = this._mixRGBandAlpha(colorNum, this._alpha * alpha);
        if (!this._drawTriUseAbsMatrix) {
            if (!matrix) {
                tmpMat.a = 1;
                tmpMat.b = 0;
                tmpMat.c = 0;
                tmpMat.d = 1;
                tmpMat.tx = x;
                tmpMat.ty = y;
            }
            else {
                tmpMat.a = matrix.a;
                tmpMat.b = matrix.b;
                tmpMat.c = matrix.c;
                tmpMat.d = matrix.d;
                tmpMat.tx = matrix.tx + x;
                tmpMat.ty = matrix.ty + y;
            }
            Matrix.mul(tmpMat, this._curMat, tmpMat);
            this.appendData(vertices, indices, vertexResult, submit, uvs, rgba, tmpMat, null, true, colors, uvRange);
        }
        else {
            let m = this._curMat == matrix ? (this._matrixChanged ? this._curMat : null) : matrix;
            this.appendData(vertices, indices, vertexResult, submit, uvs, rgba, m, null, true, colors, uvRange);
        }
        this._appendBlockInfo(vertexResult);
        if (blendMode != null) {
            this.globalCompositeOperation = oldcomp;
        }
    }
    transform(a, b, c, d, tx, ty) {
        SaveTransform.save(this);
        Matrix.mul(Matrix.TEMP.setTo(a, b, c, d, tx, ty), this._curMat, this._curMat);
        this._curMat._checkTransform();
        this._matrixChanged = true;
    }
    rotate(angle) {
        SaveTransform.save(this);
        this._curMat.rotateEx(angle);
        this._matrixChanged = true;
    }
    scale(scaleX, scaleY) {
        SaveTransform.save(this);
        this._curMat.scaleEx(scaleX, scaleY);
        this._matrixChanged = true;
    }
    clipRect(x, y, width, height, escape) {
        SaveClipRect.save(this);
        if (this._clipRect === SaveClipRect.MAX) {
            this._clipRect = new Rectangle(x, y, width, height);
        }
        else {
            this._clipRect.width = width;
            this._clipRect.height = height;
            this._clipRect.x = x;
            this._clipRect.y = y;
        }
        this._clipID_Gen++;
        this._clipID_Gen %= 10000;
        this._clipInfoID = this._clipID_Gen;
        if (escape) {
            defaultClipMatrix.copyTo(this._globalClipMatrix);
            return;
        }
        var cm = this._globalClipMatrix;
        var minx = cm.tx;
        var miny = cm.ty;
        var maxx = minx + cm.a;
        var maxy = miny + cm.d;
        let mat = this.sprite.globalTrans.getMatrix();
        if (this._clipRect.width >= Const.MAX_CLIP_SIZE) {
            cm.a = cm.d = Const.MAX_CLIP_SIZE;
            cm.b = cm.c = cm.tx = cm.ty = 0;
        }
        else {
            let { x, y, width, height } = this._clipRect;
            cm.tx = x * mat.a + y * mat.c + mat.tx;
            cm.ty = x * mat.b + y * mat.d + mat.ty;
            cm.a = width * mat.a;
            cm.b = width * mat.b;
            cm.c = height * mat.c;
            cm.d = height * mat.d;
        }
        if (cm.a > 0 && cm.d > 0) {
            var cmaxx = cm.tx + cm.a;
            var cmaxy = cm.ty + cm.d;
            if (cmaxx <= minx || cmaxy <= miny || cm.tx >= maxx || cm.ty >= maxy) {
                cm.a = -0.1;
                cm.d = -0.1;
            }
            else {
                if (cm.tx < minx) {
                    cm.a -= (minx - cm.tx);
                    cm.tx = minx;
                }
                if (cmaxx > maxx) {
                    cm.a -= (cmaxx - maxx);
                }
                if (cm.ty < miny) {
                    cm.d -= (miny - cm.ty);
                    cm.ty = miny;
                }
                if (cmaxy > maxy) {
                    cm.d -= (cmaxy - maxy);
                }
                if (cm.a <= 0)
                    cm.a = -0.1;
                if (cm.d <= 0)
                    cm.d = -0.1;
            }
        }
        this._clip_x = mat.tx;
        this._clip_y = mat.ty;
    }
    beginPath(convex = false) {
        this._getPath().beginPath(convex);
    }
    closePath() {
        this._path.closePath();
    }
    addPath(points, close, convex, dx, dy) {
        let sz = points.length;
        for (let i = 0; i < sz - 1; i += 2) {
            points[i] += dx;
            points[i + 1] += dy;
        }
        if (close && sz > 5 && (points[sz - 2] != points[0] || points[sz - 1] != points[1])) {
            points.push(points[0], points[1]);
        }
        this._getPath().push(points, convex);
    }
    fill() {
        var m = this._curMat;
        var tPath = this._getPath();
        var submit = this._curSubmit;
        let mesh;
        var sameKey = (submit._key.blendShader === this._nBlendType)
            && !this.isSameClipInfo(submit);
        var rgba = this.mixRGBandAlpha(this._fillStyle._color.numColor);
        var curEleNum = 0;
        var idx;
        let curvert = 0;
        for (var i = 0, sz = tPath.paths.length; i < sz; i++) {
            var p = tPath.paths[i];
            var vertNum = p.path.length / 2;
            if (vertNum < 3 || (vertNum === 3 && !p.convex))
                continue;
            let cpath = p.path.concat();
            let pi = 0;
            let xp, yp;
            let _x, _y;
            if (this._matrixChanged) {
                if (m._bTransform) {
                    for (pi = 0; pi < vertNum; pi++) {
                        xp = pi << 1;
                        yp = xp + 1;
                        _x = cpath[xp];
                        _y = cpath[yp];
                        cpath[xp] = m.a * _x + m.c * _y + m.tx;
                        cpath[yp] = m.b * _x + m.d * _y + m.ty;
                    }
                }
                else {
                    for (pi = 0; pi < vertNum; pi++) {
                        xp = pi << 1;
                        yp = xp + 1;
                        _x = cpath[xp];
                        _y = cpath[yp];
                        cpath[xp] = _x + m.tx;
                        cpath[yp] = _y + m.ty;
                    }
                }
            }
            let vertexResult;
            if (mesh) {
                vertexResult = mesh.checkVertex(vertNum);
            }
            if (!sameKey
                || !vertexResult) {
                curEleNum = 0;
                curvert = 0;
                vertexResult = this.acquire(vertNum);
                mesh = vertexResult.mesh;
                if (!sameKey || this._curSubmit.mesh !== mesh) {
                    submit = this._curSubmit = this.addVGSubmit(mesh);
                    sameKey = true;
                }
            }
            if (p.convex) {
                var faceNum = vertNum - 2;
                idx = new Array(faceNum * 3);
                var idxpos = 0;
                for (var fi = 0; fi < faceNum; fi++) {
                    idx[idxpos++] = curvert;
                    idx[idxpos++] = fi + 1 + curvert;
                    idx[idxpos++] = fi + 2 + curvert;
                }
            }
            else {
                idx = Earcut.earcut(cpath, null, 2);
                if (curvert > 0) {
                    for (var ii = 0; ii < idx.length; ii++) {
                        idx[ii] += curvert;
                    }
                }
            }
            this.appendData(cpath, idx, vertexResult, submit, null, rgba, null, null, false);
            curEleNum += idx.length;
            this._appendBlockInfo(vertexResult);
        }
    }
    addVGSubmit(mesh) {
        var submit = this.createSubmit(mesh);
        this._setClipInfo(submit._internalInfo);
        submit.clipInfoID = this._clipInfoID;
        return submit;
    }
    stroke() {
        if (this.lineWidth <= 0)
            return;
        var rgba = this.mixRGBandAlpha(this.strokeStyle._color.numColor);
        var tPath = this._getPath();
        var submit = this._curSubmit;
        var sameKey = (submit._key.blendShader === this._nBlendType)
            && !this.isSameClipInfo(submit);
        let mesh = this._meshPool[this._currentMeshIndex];
        var curEleNum = 0;
        let m = this._curMat;
        for (var i = 0, sz = tPath.paths.length; i < sz; i++) {
            var p = tPath.paths[i];
            if (p.path.length <= 0)
                continue;
            var idx = [];
            var vertex = [];
            var maxVertexNum = p.path.length * 2;
            if (maxVertexNum < 2)
                continue;
            BasePoly.createLine2(p.path, idx, this.lineWidth, 0, vertex, p.loop);
            let ptnum = vertex.length / 2;
            let vertexResult;
            if (mesh) {
                vertexResult = mesh.checkVertex(ptnum);
            }
            if (!sameKey
                || !vertexResult) {
                curEleNum = 0;
                vertexResult = this.acquire(ptnum);
                mesh = vertexResult.mesh;
                if (!sameKey || this._curSubmit.mesh !== mesh) {
                    submit = this._curSubmit = this.addVGSubmit(mesh);
                    sameKey = true;
                }
            }
            let pi = 0;
            let xp, yp;
            let _x, _y;
            if (this._matrixChanged) {
                if (m._bTransform) {
                    for (pi = 0; pi < ptnum; pi++) {
                        xp = pi << 1;
                        yp = xp + 1;
                        _x = vertex[xp];
                        _y = vertex[yp];
                        vertex[xp] = m.a * _x + m.c * _y + m.tx;
                        vertex[yp] = m.b * _x + m.d * _y + m.ty;
                    }
                }
                else {
                    for (pi = 0; pi < ptnum; pi++) {
                        xp = pi << 1;
                        yp = xp + 1;
                        _x = vertex[xp];
                        _y = vertex[yp];
                        vertex[xp] = _x + m.tx;
                        vertex[yp] = _y + m.ty;
                    }
                }
            }
            this.appendData(vertex, idx, vertexResult, submit, null, rgba, null, null, false);
            curEleNum += idx.length;
            this._appendBlockInfo(vertexResult);
        }
    }
    moveTo(x, y) {
        var tPath = this._getPath();
        tPath.newPath();
        tPath._lastOriX = x;
        tPath._lastOriY = y;
        tPath.addPoint(x, y);
    }
    lineTo(x, y) {
        var tPath = this._getPath();
        if (Math.abs(x - tPath._lastOriX) < 1e-3 && Math.abs(y - tPath._lastOriY) < 1e-3)
            return;
        tPath._lastOriX = x;
        tPath._lastOriY = y;
        tPath.addPoint(x, y);
    }
    arcTo(x1, y1, x2, y2, r) {
        var i = 0;
        var x = 0, y = 0;
        var dx = this._path._lastOriX - x1;
        var dy = this._path._lastOriY - y1;
        var len1 = Math.sqrt(dx * dx + dy * dy);
        if (len1 <= 0.000001) {
            return;
        }
        var ndx = dx / len1;
        var ndy = dy / len1;
        var dx2 = x2 - x1;
        var dy2 = y2 - y1;
        var len22 = dx2 * dx2 + dy2 * dy2;
        var len2 = Math.sqrt(len22);
        if (len2 <= 0.000001) {
            return;
        }
        var ndx2 = dx2 / len2;
        var ndy2 = dy2 / len2;
        var odx = ndx + ndx2;
        var ody = ndy + ndy2;
        var olen = Math.sqrt(odx * odx + ody * ody);
        if (olen <= 0.000001) {
            return;
        }
        var nOdx = odx / olen;
        var nOdy = ody / olen;
        var alpha = Math.acos(nOdx * ndx + nOdy * ndy);
        var halfAng = Math.PI / 2 - alpha;
        len1 = r * Math.tan(halfAng);
        var ptx1 = len1 * ndx + x1;
        var pty1 = len1 * ndy + y1;
        var orilen = Math.sqrt(len1 * len1 + r * r);
        var orix = x1 + nOdx * orilen;
        var oriy = y1 + nOdy * orilen;
        var ptx2 = len1 * ndx2 + x1;
        var pty2 = len1 * ndy2 + y1;
        var dir = ndx * ndy2 - ndy * ndx2;
        var fChgAng = 0;
        var sinx = 0.0;
        var cosx = 0.0;
        if (dir >= 0) {
            fChgAng = halfAng * 2;
            var fda = fChgAng / GraphicsRunner.SEGNUM;
            sinx = Math.sin(fda);
            cosx = Math.cos(fda);
        }
        else {
            fChgAng = -halfAng * 2;
            fda = fChgAng / GraphicsRunner.SEGNUM;
            sinx = Math.sin(fda);
            cosx = Math.cos(fda);
        }
        var lastx = this._path._lastOriX, lasty = this._path._lastOriY;
        var _x1 = ptx1, _y1 = pty1;
        if (Math.abs(_x1 - this._path._lastOriX) > 0.1 || Math.abs(_y1 - this._path._lastOriY) > 0.1) {
            x = _x1;
            y = _y1;
            lastx = _x1;
            lasty = _y1;
            this._path._lastOriX = x;
            this._path._lastOriY = y;
            this._path.addPoint(x, y);
        }
        var cvx = ptx1 - orix;
        var cvy = pty1 - oriy;
        var tx = 0.0;
        var ty = 0.0;
        for (i = 0; i < GraphicsRunner.SEGNUM; i++) {
            var cx = cvx * cosx + cvy * sinx;
            var cy = -cvx * sinx + cvy * cosx;
            x = cx + orix;
            y = cy + oriy;
            if (Math.abs(lastx - x) > 0.1 || Math.abs(lasty - y) > 0.1) {
                this._path._lastOriX = x;
                this._path._lastOriY = y;
                this._path.addPoint(x, y);
                lastx = x;
                lasty = y;
            }
            cvx = cx;
            cvy = cy;
        }
    }
    arc(cx, cy, rx, ry, startAngle, endAngle, counterclockwise = false, b = true, minNum = 20) {
        if (startAngle > endAngle) {
            [startAngle, endAngle] = [endAngle, startAngle];
        }
        let da = endAngle - startAngle;
        if (!counterclockwise) {
            if (Math.abs(da) >= Math.PI * 2) {
                da = Math.PI * 2;
            }
            else {
                while (da < 0.0) {
                    da += Math.PI * 2;
                }
            }
        }
        else {
            if (Math.abs(da) >= Math.PI * 2) {
                da = -Math.PI * 2;
            }
            else {
                while (da > 0.0) {
                    da -= Math.PI * 2;
                }
            }
        }
        var sx = this.getCurrentScaleX();
        var sy = this.getCurrentScaleY();
        var sr = rx * (sx > sy ? sx : sy);
        var cl = 2 * Math.PI * sr;
        let ndivs = (Math.max(cl / 5, minNum)) | 0;
        let stepAng = Math.PI * 2 / ndivs;
        var tPath = this._getPath();
        let x = cx + Math.cos(startAngle) * rx;
        let y = cy + Math.sin(startAngle) * ry;
        if (x != this._path._lastOriX || y != this._path._lastOriY) {
            tPath.addPoint(x, y);
        }
        let curAng = Math.ceil(startAngle / stepAng) * stepAng;
        while (endAngle - curAng >= stepAng) {
            x = cx + Math.cos(curAng) * rx;
            y = cy + Math.sin(curAng) * ry;
            tPath.addPoint(x, y);
            curAng += stepAng;
        }
        x = cx + Math.cos(endAngle) * rx;
        y = cy + Math.sin(endAngle) * ry;
        if (x != this._path._lastOriX || y != this._path._lastOriY) {
            tPath.addPoint(x, y);
        }
    }
    quadraticCurveTo(cpx, cpy, x, y) {
        var tArray = Bezier.getPoints([this._path._lastOriX, this._path._lastOriY, cpx, cpy, x, y], 30, 2);
        for (var i = 0, n = tArray.length / 2; i < n; i++) {
            this.lineTo(tArray[i * 2], tArray[i * 2 + 1]);
        }
        this.lineTo(x, y);
    }
    mixRGBandAlpha(color) {
        return this._mixRGBandAlpha(color, this._alpha);
    }
    _mixRGBandAlpha(color, alpha) {
        if (alpha >= 1) {
            return color;
        }
        var a = ((color & 0xff000000) >>> 24);
        if (a != 0) {
            a *= alpha;
        }
        else {
            a = alpha * 255;
        }
        return (color & 0x00ffffff) | (a << 24);
    }
    strokeRect(x, y, width, height, parameterLineWidth = 0) {
        var tW = parameterLineWidth * 0.5;
        if (this.lineWidth > 0) {
            var rgba = this.mixRGBandAlpha(this.strokeStyle._color.numColor);
            var hw = this.lineWidth / 2;
            this._fillRect(x - hw, y - hw, width + this.lineWidth, this.lineWidth, rgba);
            this._fillRect(x - hw, y - hw + height, width + this.lineWidth, this.lineWidth, rgba);
            this._fillRect(x - hw, y + hw, this.lineWidth, height - this.lineWidth, rgba);
            this._fillRect(x - hw + width, y + hw, this.lineWidth, height - this.lineWidth, rgba);
        }
    }
    drawParticle(x, y, pt) {
    }
    _getPath() {
        return this._path || (this._path = new Path());
    }
    _getImageSource(texture) {
        let cs = this.sprite;
        return texture._getSource(function () {
            if (cs) {
                if (cs._graphics)
                    cs._graphics.repaint();
                else
                    cs.repaint();
            }
        });
    }
    acquire(vertexCount) {
        let meshes = this._meshPool;
        for (let i = 0; i < meshes.length; i++) {
            let mesh = meshes[i];
            let result = mesh.checkVertex(vertexCount);
            if (result) {
                this._currentMeshIndex = i;
                return result;
            }
        }
        let mesh = new GraphicsMesh();
        this._meshPool.push(mesh);
        this._currentMeshIndex = this._meshPool.length - 1;
        let result = mesh.checkVertex(vertexCount);
        return result;
    }
    appendData(vertices, indices, result, submit, uvs, rgba, matrix, uvrect, useTex, colors, uvRange) {
        let vertexCount = vertices.length / 2;
        let uvminx = 0;
        let uvminy = 0;
        let uvu = 1;
        let uvv = 1;
        if (uvrect) {
            uvminx = uvrect[0];
            uvminy = uvrect[1];
            uvu = uvrect[2];
            uvv = uvrect[3];
        }
        let m00, m01, m10, m11, tx, ty;
        if (matrix) {
            m00 = matrix.a;
            m01 = matrix.b;
            m10 = matrix.c;
            m11 = matrix.d;
            tx = matrix.tx;
            ty = matrix.ty;
        }
        let r = (rgba & 0xff) / 255.0;
        let b = ((rgba >>> 16) & 0xff) / 255.0;
        let g = ((rgba >>> 8) & 0xff) / 255.0;
        let a = (rgba >>> 24) / 255.0;
        let useTexByte = useTex ? 0xff : 0;
        let useClipByte = uvRange ? 0xff : 0;
        let dataViewIndex = 0;
        let vertexViews = result.vertexViews;
        let indexsMap = [];
        let dataView;
        let offset = 0;
        let positions = [];
        let vbdata = result.mesh._buffer._tempVertexData;
        let vertexLength = GraphicsMesh.stride;
        for (let i = 0, pi = 0, ci = 0, vi = 0; i < vertexCount; i++) {
            if (!dataView || dataView.length <= vi) {
                if (dataView) {
                    dataView.setData(vbdata);
                }
                dataView = vertexViews[dataViewIndex];
                dataViewIndex++;
                vi = 0;
                offset = dataView.start / dataView.stride;
            }
            let x = vertices[pi], y = vertices[pi + 1];
            if (matrix) {
                if (matrix._bTransform) {
                    vbdata[vi] = positions[pi] = x * m00 + y * m10 + tx;
                    vbdata[vi + 1] = positions[pi + 1] = x * m01 + y * m11 + ty;
                }
                else {
                    vbdata[vi] = positions[pi] = x + tx;
                    vbdata[vi + 1] = positions[pi + 1] = y + ty;
                }
            }
            else {
                vbdata[vi] = positions[pi] = x;
                vbdata[vi + 1] = positions[pi + 1] = y;
            }
            if (uvs) {
                vbdata[vi + 2] = uvminx + uvs[pi] * uvu;
                vbdata[vi + 3] = uvminy + uvs[pi + 1] * uvv;
            }
            if (colors != null) {
                vbdata[vi + 4] = colors[ci];
                vbdata[vi + 5] = colors[ci + 1];
                vbdata[vi + 6] = colors[ci + 2];
                vbdata[vi + 7] = colors[ci + 3];
            }
            else {
                vbdata[vi + 4] = r;
                vbdata[vi + 5] = g;
                vbdata[vi + 6] = b;
                vbdata[vi + 7] = a;
            }
            vbdata[vi + 8] = useTexByte;
            vbdata[vi + 9] = useClipByte;
            if (uvRange) {
                vbdata[vi + 12] = uvRange[0];
                vbdata[vi + 13] = uvRange[1];
                vbdata[vi + 14] = uvRange[2];
                vbdata[vi + 15] = uvRange[3];
            }
            vi += vertexLength;
            pi += 2;
            ci += 4;
            indexsMap[i] = offset++;
        }
        if (dataView) {
            dataView.setData(vbdata);
        }
        result.positions = positions;
        let indexOffset = submit.indexCount;
        let indexCount = indices.length;
        let ibdata = submit.indices;
        for (let i = 0; i < indexCount; i++) {
            ibdata[i + indexOffset] = indexsMap[indices[i]];
        }
        submit.indexCount += indexCount;
    }
    initDefalutMesh() {
        if (!this.def_geometry) {
            let length = 4 * GraphicsMesh.stride;
            let def_vertices = new Float32Array(length);
            let inv_vertices = new Float32Array(length);
            let def_uv = Texture.DEF_UV;
            let inv_uv = Texture.INV_UV;
            let positions = [0, 0, 1, 0, 1, 1, 0, 1];
            let offset = 0;
            for (let i = 0; i < 4; i++) {
                let index = i * GraphicsMesh.stride;
                inv_vertices[index] = def_vertices[index] = positions[offset];
                inv_vertices[index + 1] = def_vertices[index + 1] = positions[offset + 1];
                def_vertices[index + 2] = def_uv[offset];
                def_vertices[index + 3] = def_uv[offset + 1];
                inv_vertices[index + 2] = inv_uv[offset];
                inv_vertices[index + 3] = inv_uv[offset + 1];
                inv_vertices[index + 4] = def_vertices[index + 4] = 1;
                inv_vertices[index + 5] = def_vertices[index + 5] = 1;
                inv_vertices[index + 6] = def_vertices[index + 6] = 1;
                inv_vertices[index + 7] = def_vertices[index + 7] = 1;
                inv_vertices[index + 8] = def_vertices[index + 8] = 0xff;
                offset += 2;
            }
            let indices = _drawTexToQuad_Index;
            let indexBuffer = LayaGL.renderDeviceFactory.createIndexBuffer(BufferUsage.Static);
            indexBuffer.indexType = IndexFormat.UInt16;
            indexBuffer.indexCount = indices.length;
            indexBuffer._setIndexDataLength(indices.byteLength);
            indexBuffer._setIndexData(indices, 0);
            let defaultBuffer = LayaGL.renderDeviceFactory.createVertexBuffer(BufferUsage.Static);
            defaultBuffer.vertexDeclaration = GraphicsMesh.vertexDeclarition;
            defaultBuffer.setDataLength(def_vertices.byteLength);
            defaultBuffer.setData(def_vertices.buffer, 0, 0, def_vertices.byteLength);
            let invertBuffer = LayaGL.renderDeviceFactory.createVertexBuffer(BufferUsage.Static);
            invertBuffer.vertexDeclaration = GraphicsMesh.vertexDeclarition;
            invertBuffer.setDataLength(inv_vertices.byteLength);
            invertBuffer.setData(inv_vertices.buffer, 0, 0, inv_vertices.byteLength);
            this.def_geometry = LayaGL.renderDeviceFactory.createRenderGeometryElement(MeshTopology.Triangles, DrawType.DrawElement);
            let defaultState = LayaGL.renderDeviceFactory.createBufferState();
            defaultState.applyState([defaultBuffer], indexBuffer);
            this.def_geometry.bufferState = defaultState;
            this.def_geometry.indexFormat = IndexFormat.UInt16;
            this.def_geometry.setDrawElemenParams(6, 0);
            this.inv_geometry = LayaGL.renderDeviceFactory.createRenderGeometryElement(MeshTopology.Triangles, DrawType.DrawElement);
            let invertState = LayaGL.renderDeviceFactory.createBufferState();
            invertState.applyState([invertBuffer], indexBuffer);
            this.inv_geometry.bufferState = invertState;
            this.inv_geometry.indexFormat = IndexFormat.UInt16;
            this.inv_geometry.setDrawElemenParams(6, 0);
        }
    }
}
GraphicsRunner.SEGNUM = 32;
class ContextParams {
    constructor() {
        this.lineWidth = 1;
    }
    clear() {
        this.lineWidth = 1;
        this.textAlign = this.textBaseline = null;
    }
    make() {
        return this === ContextParams.DEFAULT ? new ContextParams() : this;
    }
}
ContextParams.DEFAULT = new ContextParams();
